// Dependencies
import React from 'react';
import uuid from 'react-uuid';
import parse from 'html-react-parser';
import PropTypes from 'prop-types';
import Masonry from 'react-masonry-component';
import { Link } from 'gatsby';

// Components
import Container from '../../container/container';
import Image from '../../image/image';
import Slider from '../../slider/slider';

// Hooks
import useEndorsementsPostType from '../../../hooks/useEndorsementsPostType';

// Styles
import EndorsementsStyles from './endorsements.styles';

export default function Endorsements({ layout }) {
  const backgroundImage = layout?.backgroundImage;

  const endorsements = useEndorsementsPostType();
  let filteredEndorsements = endorsements;

  const heading = layout?.heading;
  const text = layout?.text;
  const filterBy = layout?.filterby;
  const locationFilter = layout?.locationFilter || {};
  const manualSelectionFilter = layout?.manualSelectionFilter || [];
  const displayButton = layout?.displayReadMoreButton;
  const slider = layout?.slider;
  const condensed = layout?.condensed;

  if (filterBy === 'location' && locationFilter && locationFilter?.slug) {
    filteredEndorsements = filteredEndorsements.filter((endorsement) =>
      endorsement?.locations?.nodes.some(
        (location) => location?.slug === locationFilter?.slug,
      ),
    );
  }

  const filterIds = [];
  manualSelectionFilter.forEach((filterId) => {
    filterIds.push(filterId?.id);
  });

  if (filterBy === 'selection' && manualSelectionFilter.length) {
    filteredEndorsements = filteredEndorsements.filter((endorsement) =>
      filterIds.includes(endorsement?.id),
    );
  }

  const endorsementsLoop = (
    <>
      {filteredEndorsements.map((endorsement) => {
        const name = endorsement?.title;
        const age = endorsement?.endorsements?.age;
        const locations = endorsement?.locations?.nodes || [];
        const descriptor = endorsement?.endorsements?.descriptor;
        const quote = endorsement?.endorsements?.quote;

        return (
          <li key={uuid()} className="endorsement">
            <span className="inner">
              <svg width="40" height="30" fill="#535e6b" className="quote-mark">
                <path d="M 24.295394,26.863758 C 19.200505,21.145204 22.710131,11.190578 26.951762,7.3424212 C 30.566689,4.0628338 36.556731,1.3813774 37.121428,2.3183024 C 38.019154,3.8077781 34.48577,4.865211 31.829231,7.2558366 C 28.512946,10.842074 27.399362,12.454202 31.988064,14.477295 C 36.906646,16.697086 37.001109,18.868305 37.159152,21.630467 C 37.55703,28.584322 29.269122,31.837485 24.295394,26.863758 z " />
                <path d="M 5.1231491,26.764559 C 0.028260593,21.046005 3.5378861,11.091379 7.7795171,7.2432227 C 11.394445,3.9636353 17.384487,1.2821789 17.949184,2.2191039 C 18.84691,3.7085796 15.313526,4.7660125 12.656987,7.1566381 C 9.3407014,10.742875 8.2271171,12.355003 12.81582,14.378096 C 17.734402,16.597887 17.828865,18.769106 17.986908,21.531268 C 18.384786,28.485123 10.096878,31.738286 5.1231491,26.764559 z " />
              </svg>

              <div className="quote">{parse(quote)}</div>

              <h4 className="meta">
                <span className="dash">&mdash; </span>
                {name && <span className="name">{name}, </span>}
                {age && <span className="age">{age}, </span>}
                {descriptor && (
                  <span className="descriptor">{descriptor}, </span>
                )}

                {locations.map((location) => (
                  <span className="location" key={uuid()}>
                    {location?.name}
                  </span>
                ))}
              </h4>
            </span>
          </li>
        );
      })}
    </>
  );

  return (
    <EndorsementsStyles backgroundImage condensed={condensed}>
      {(backgroundImage?.gatsbyImage ||
        backgroundImage?.localFile?.publicURL) && (
        <Image image={backgroundImage} background />
      )}

      {!!heading || !!text ? (
        <div className="width-small">
          <Container>
            <header className="header">
              {!!heading && (
                <h2 className="heading large">
                  {heading}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                    fill="#535e6b"
                    className="heart"
                  >
                    <path d="M256 112L270.2 94.2C300.1 55.8 350.1 37.01 398.6 45.1C464 56.01 512 112.6 512 179V190.9C512 232.4 494.8 272.1 464.4 300.4L283.7 469.1C276.2 476.1 266.3 480 256 480C245.7 480 235.8 476.1 228.3 469.1L47.59 300.4C17.23 272.1 0 232.4 0 190.9V179C0 112.6 47.98 56.01 113.4 45.1C161.9 37.01 211 55.79 241.8 94.2L256 112zM256 137.6L229.3 104.2C202.2 70.32 158.9 53.75 116.1 60.88C58.32 70.5 16 120.5 16 179V190.9C16 227.1 31.39 263.4 58.51 288.7L239.2 457.4C243.8 461.6 249.8 464 256 464C262.2 464 268.2 461.6 272.8 457.4L453.5 288.7C480.6 263.4 496 227.1 496 190.9V179C496 120.5 453.7 70.5 395.9 60.88C353.1 53.75 309.8 70.32 282.7 104.2L256 137.6z" />
                  </svg>
                </h2>
              )}

              {!!text && parse(text)}
            </header>
          </Container>
        </div>
      ) : (
        ''
      )}

      <Container>
        <div>
          {filterBy !== 'selection' && (
            <Masonry elementType="ul" className="endorsements masonry">
              {endorsementsLoop}
            </Masonry>
          )}

          {filterBy === 'selection' && slider && (
            <Slider
              className="endorsements selection-slider"
              slides={1}
              gap={2}
              dots
              breakpoints={{
                375: { slides: 1, gap: 2 },
                768: { slides: 2, gap: 2 },
                992: { slides: 3, gap: 2 },
                1200: { slides: 3, gap: 2 },
                1920: { slides: 3, gap: 2 },
              }}
            >
              {endorsementsLoop}
            </Slider>
          )}

          {(filterBy === 'selection' && !slider) ||
            (filterBy === 'location' && (
              <ul className="endorsements selection">{endorsementsLoop}</ul>
            ))}
        </div>
      </Container>

      {filterBy === 'selection' &&
        manualSelectionFilter.length &&
        displayButton && (
          <Container flex alignItems="center" justifyContent="center">
            <Link to="/success-stories" className="btn dark read-more">
              Read More
            </Link>
          </Container>
        )}
    </EndorsementsStyles>
  );
}

Endorsements.propTypes = {
  layout: PropTypes.shape({
    heading: PropTypes.string,
    text: PropTypes.string,
    filterby: PropTypes.string,
    locationFilter: PropTypes.shape({}),
    manualSelectionFilter: PropTypes.arrayOf(PropTypes.shape({})),
    displayReadMoreButton: PropTypes.bool,
    slider: PropTypes.bool,
    condensed: PropTypes.bool,
    backgroundImage: PropTypes.shape({
      altText: PropTypes.string,
      gatsbyImage: PropTypes.shape({}),
    }),
  }),
};

Endorsements.defaultProps = {
  layout: {},
};
